import React from "react";
import {useKeycloak} from "@react-keycloak/web";
import {useTranslation} from "react-i18next";
import {
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemText,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import LinkOffIcon from "@material-ui/icons/LinkOff";
import {Loading} from "../../common/Loading";
import {ServerCommunicationAlert} from "../../common/errors";
import API, {createApiConfig} from "../../../utils/API";
import {AxiosError} from "axios";
import {
    MunicipalityListItemDTO,
    OrganizationDTO,
    OrganizationProfileDTO,
    municipalityTypes,
} from "../../../models/organization";
import ROUTES from "../../../routes/routes";
import {Link, useParams} from "react-router-dom";
import {BreadcrumbItem, BreadcrumbsRow} from "../../common/breadcrumbs";
import {PageAction, PageHeader} from "../../common/headers";
import {isAdmin} from "../../../utils/auth";

interface SubOrganizationsParams {
    organizationId: string;
}

type PageState =
    | {name: "loading"}
    | {name: "loaded"; organizations: OrganizationDTO[]; parentOrg: OrganizationDTO}
    | {name: "failed"};

type SearchState =
    | {name: "idle"}
    | {name: "searching"}
    | {name: "done"; results: MunicipalityListItemDTO[]}
    | {name: "error"};

const searchableTypes = municipalityTypes.filter((t) => t !== "KRAJ");

const SubOrganizations: React.FC = () => {
    const [t] = useTranslation("organization");
    const {organizationId} = useParams<SubOrganizationsParams>();
    const {keycloak, initialized} = useKeycloak();
    const [state, setState] = React.useState<PageState>({name: "loading"});

    // Add dialog
    const [addDialogOpen, setAddDialogOpen] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState("");
    const [searchState, setSearchState] = React.useState<SearchState>({name: "idle"});
    const [linking, setLinking] = React.useState(false);
    const [linkError, setLinkError] = React.useState<string | null>(null);

    // Unlink dialog
    const [unlinkTarget, setUnlinkTarget] = React.useState<OrganizationDTO | null>(null);
    const [unlinking, setUnlinking] = React.useState(false);

    React.useEffect(() => {
        if (state.name !== "loading") return;
        API.get<OrganizationProfileDTO>(
            `/organizations/profile/${organizationId}`,
            createApiConfig(keycloak, initialized)
        )
            .then((res) => {
                setState({
                    name: "loaded",
                    organizations: res.data.subjectMunicipalities
                        .slice()
                        .sort((a, b) => a.name.localeCompare(b.name, "cs")),
                    parentOrg: res.data.organization,
                });
            })
            .catch((_err: AxiosError) => {
                setState({name: "failed"});
            });
    }, [keycloak, initialized, state, organizationId]);

    if (state.name === "loading") return <Loading />;
    if (state.name === "failed") return <ServerCommunicationAlert />;

    const admin = isAdmin(keycloak);
    const parentMun = state.parentOrg.municipality;

    // --- Add handlers ---
    const openAddDialog = () => {
        setSearchQuery("");
        setSearchState({name: "idle"});
        setLinkError(null);
        setAddDialogOpen(true);
    };

    const handleSearch = () => {
        if (searchQuery.trim().length < 2) return;
        setSearchState({name: "searching"});
        const typesParams = searchableTypes.map((t) => `types=${t}`).join("&");
        API.get<{content: MunicipalityListItemDTO[]}>(
            `/organizations/search?name=${encodeURIComponent(searchQuery.trim())}&${typesParams}&size=20`,
            createApiConfig(keycloak, initialized)
        )
            .then((res) => setSearchState({name: "done", results: res.data.content}))
            .catch(() => setSearchState({name: "error"}));
    };

    const handleLink = (candidate: MunicipalityListItemDTO) => {
        setLinking(true);
        setLinkError(null);
        API.put(
            `/organizations/${candidate.publicId}/parent?parentOrganizationId=${state.parentOrg.publicId}`,
            {},
            createApiConfig(keycloak, initialized)
        )
            .then(() => {
                setAddDialogOpen(false);
                setLinking(false);
                setState({name: "loading"});
            })
            .catch((err: AxiosError) => {
                setLinking(false);
                if (err.response?.status === 422) {
                    const code: string = err.response?.data?.code ?? "";
                    if (code === "CIRCULAR_RELATIONSHIP") {
                        setLinkError(t("organization.errorCircularRelationship"));
                    } else {
                        setLinkError(t("organization.errorAlreadyHasParent"));
                    }
                } else {
                    setLinkError(t("common:app.comm_error"));
                }
            });
    };

    // --- Unlink handlers ---
    const handleUnlink = () => {
        if (!unlinkTarget) return;
        setUnlinking(true);
        API.delete(`/organizations/${unlinkTarget.publicId}/parent`, createApiConfig(keycloak, initialized))
            .then(() => {
                setUnlinkTarget(null);
                setUnlinking(false);
                setState({name: "loading"});
            })
            .catch(() => {
                setUnlinking(false);
            });
    };

    return (
        <Container>
            <BreadcrumbsRow>
                <BreadcrumbItem name={state.parentOrg.name} route={ROUTES.organization.create(organizationId)} />
                <BreadcrumbItem
                    name={t("organization.subOrganizations")}
                    route={ROUTES.subOrganizations.create(organizationId)}
                />
            </BreadcrumbsRow>
            <PageHeader title={t("organization.subOrganizations")}>
                {admin && parentMun?.id && (
                    <PageAction title={t("organization.addSubOrganization")} onClick={openAddDialog} last>
                        <AddIcon fontSize="small" />
                        {t("organization.addSubOrganization")}
                    </PageAction>
                )}
            </PageHeader>

            <Container>
                {state.organizations.length === 0 ? (
                    <Box style={{textAlign: "center", marginTop: "2em"}}>
                        <Typography variant="body1" color="textSecondary">
                            {t("organization.noSubOrganizations")}
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={3} direction="row">
                        {state.organizations.map((org: OrganizationDTO) => (
                            <Grid item md={4} xs={12} key={org.id}>
                                <Card style={{height: "100%", display: "flex", flexDirection: "column"}}>
                                    <CardActionArea
                                        component={Link}
                                        to={ROUTES.organization.create(org.publicId)}
                                        style={{flexGrow: 1}}
                                    >
                                        <CardContent>
                                            <Typography gutterBottom variant="h5" component="h2">
                                                {org.name}
                                            </Typography>
                                            {org.municipality && (
                                                <Typography variant="body2" color="textSecondary">
                                                    {t("municipalityType." + org.municipality.type)}
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </CardActionArea>
                                    {admin && (
                                        <Box
                                            display="flex"
                                            justifyContent="flex-end"
                                            style={{borderTop: "1px solid #E5E7EB", padding: "4px 8px"}}
                                        >
                                            <Tooltip title={t("organization.unlinkSubOrganization") as string}>
                                                <IconButton size="small" onClick={() => setUnlinkTarget(org)}>
                                                    <LinkOffIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    )}
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Container>

            {/* Add dialog */}
            <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{t("organization.addSubOrganization")}</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" style={{gap: "12px", paddingTop: "8px"}}>
                        <Box display="flex" style={{gap: "8px"}}>
                            <TextField
                                label={t("organization.query")}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                                variant="outlined"
                                fullWidth
                                size="small"
                            />
                            <Button
                                onClick={handleSearch}
                                variant="contained"
                                color="primary"
                                disabled={searchQuery.trim().length < 2 || searchState.name === "searching"}
                                style={{whiteSpace: "nowrap"}}
                            >
                                {t("common:actions.show")}
                            </Button>
                        </Box>

                        {searchState.name === "searching" && (
                            <Box display="flex" justifyContent="center" style={{padding: "16px"}}>
                                <CircularProgress size={24} />
                            </Box>
                        )}

                        {searchState.name === "done" && searchState.results.length === 0 && (
                            <Typography variant="body2" color="textSecondary" align="center">
                                {t("organization.select.empty")}
                            </Typography>
                        )}

                        {searchState.name === "done" && searchState.results.length > 0 && (
                            <List
                                dense
                                style={{border: "1px solid #E5E7EB", borderRadius: 8, maxHeight: 280, overflow: "auto"}}
                            >
                                {searchState.results.map((org) => (
                                    <ListItem
                                        key={org.municipalityId}
                                        button
                                        disabled={linking}
                                        onClick={() => handleLink(org)}
                                        style={{borderBottom: "1px solid #F3F4F6"}}
                                    >
                                        <ListItemText
                                            primary={org.name}
                                            secondary={t("municipalityType." + org.type)}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        )}

                        {searchState.name === "error" && (
                            <Typography color="error" variant="body2">
                                {t("common:app.comm_error")}
                            </Typography>
                        )}

                        {linkError && (
                            <Typography color="error" variant="body2">
                                {linkError}
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions style={{padding: "16px 24px"}}>
                    <Button onClick={() => setAddDialogOpen(false)} disabled={linking}>
                        {t("common:actions.cancel")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Unlink confirmation dialog */}
            <Dialog
                open={unlinkTarget !== null}
                onClose={() => !unlinking && setUnlinkTarget(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>{t("organization.unlinkSubOrganization")}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {t("organization.unlinkSubOrganizationConfirm", {name: unlinkTarget?.name})}
                    </Typography>
                </DialogContent>
                <DialogActions style={{padding: "16px 24px"}}>
                    <Button onClick={() => setUnlinkTarget(null)} disabled={unlinking}>
                        {t("common:actions.cancel")}
                    </Button>
                    <Button onClick={handleUnlink} color="secondary" variant="contained" disabled={unlinking}>
                        {t("organization.unlinkSubOrganization")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default SubOrganizations;
